import { useCallback, useEffect, useMemo, useState } from "react";
import { autocompleteClasses } from "@mui/material";

export interface UseCloseOnScrollResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const isListScroll = (target: EventTarget | null): boolean =>
  target instanceof Element &&
  !!target.closest(`.${autocompleteClasses.listbox}`);

/** Open state for an Autocomplete whose list closes once the page scrolls, so it never floats away from its field. */
export const useCloseOnScroll = (): UseCloseOnScrollResult => {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleScroll = (event: Event) => {
      if (!isListScroll(event.target)) setIsOpen(false);
    };
    // Scroll events do not bubble, so listen in the capture phase to catch any scrolling ancestor.
    document.addEventListener("scroll", handleScroll, true);
    return () => document.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  return useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);
};
